// Ranking for image search results (Wikimedia Commons, Flickr): how closely a
// result matches the name being searched for, judged from its file name /
// title first, then its description and tags. Accent- and case-insensitive.
// Ties keep the source's own order (Wikimedia relevance rank / Flickr
// relevance) and, when the place's coordinates are known, prefer the result
// taken closer to the place.

const STOP = new Set([
  "the", "a", "an", "of", "and", "in", "at", "on", "de", "la", "le", "les", "el", "los", "las",
  "del", "di", "da", "du", "des", "et", "y", "e", "file", "jpg", "jpeg", "png",
]);

const norm = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const tokens = (s) => norm(s).split(" ").filter((t) => t.length >= 2 && !STOP.has(t));

// 0-100. Whole name found in the title +20, then the share of the name's words
// found in the title (up to 60) and in the description / tags (up to 20).
export function matchScore(query, item) {
  const q = tokens(query);
  if (q.length === 0) return 0;
  const title = norm(item.name);
  const titleTokens = new Set(tokens(item.name));
  const extraTokens = new Set(tokens(`${item.description || ""} ${item.tags || ""}`));
  const inTitle = q.filter((t) => titleTokens.has(t)).length / q.length;
  const inExtra = q.filter((t) => extraTokens.has(t)).length / q.length;
  const whole = norm(query);
  return (whole && title.includes(whole) ? 20 : 0) + inTitle * 60 + inExtra * 20;
}

const rad = (d) => (d * Math.PI) / 180;
function distanceKm(lat1, lon1, lat2, lon2) {
  const a =
    Math.sin(rad(lat2 - lat1) / 2) ** 2 +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lon2 - lon1) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(a)));
}

// Distance of a result from the place, in km: exact when the result has its own
// coordinates (Flickr: sets item.distanceKm), otherwise the ring it was found in
// (Wikimedia: item.distanceWithinKm, from the 1 / 3 / 10 km searches), else unknown.
function itemDistance(item, hasPlace, lat, lon) {
  const la = parseFloat(item.latitude);
  const lo = parseFloat(item.longitude);
  if (hasPlace && !isNaN(la) && !isNaN(lo)) {
    item.distanceKm = distanceKm(lat, lon, la, lo);
    return item.distanceKm;
  }
  if (typeof item.distanceWithinKm === "number") return item.distanceWithinKm;
  return Infinity;
}

// Best name match first, but distance counts among comparable matches: scores
// are grouped in bands of 25 points, and within a band the closer result wins;
// then the finer score, then the source's own order. (Without the banding a
// one-point score difference would always beat a much closer photo.)
export function sortByMatch(items, query, opts) {
  // opts.ignoreDistance: large places (mountains, lakes, parks) are photographed
  // from far away, so distance must not affect the order.
  const lat = opts && parseFloat(opts.lat);
  const lon = opts && parseFloat(opts.lon);
  const hasPlace = !isNaN(lat) && !isNaN(lon) && !(lat === 0 && lon === 0);
  return (items || [])
    .map((item, idx) => {
      const score = Math.round(matchScore(query, item));
      return { item, idx, score, band: Math.floor(score / 25), d: opts && opts.ignoreDistance ? 0 : itemDistance(item, hasPlace, lat, lon) };
    })
    .sort((a, b) => b.band - a.band || a.d - b.d || b.score - a.score || a.idx - b.idx)
    .map((x) => x.item);
}

// "about 400 m from the place" / "within 3 km of the place" for the details card.
export function distanceLabel(item) {
  if (typeof item.distanceKm === "number" && isFinite(item.distanceKm)) {
    return item.distanceKm < 1
      ? `about ${Math.max(10, Math.round(item.distanceKm * 100) * 10)} m from the place`
      : `about ${item.distanceKm.toFixed(1)} km from the place`;
  }
  if (typeof item.distanceWithinKm === "number") return `within ${item.distanceWithinKm} km of the place`;
  return "";
}
